<template>
    <!-- Single-select variant of section-status's FarmChipPicker: one pill button per farm, kept to a
         single line in the caller's own order, with whatever doesn't fit collapsing behind a "+N"
         overflow button. Unlike FarmChipPicker this is mutually exclusive - selecting a farm always
         replaces the current selection, never toggles it off. -->
    <div ref="farmRowRef" class="relative flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
        <button
            v-for="farm in visibleFarms"
            :key="farm.farm_id"
            type="button"
            class="shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors"
            :class="isSelected(farm.farm_id) ? 'border-primary-700 bg-primary-700 text-white' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-100'"
            @click="selectFarm(farm.farm_id)"
        >
            {{ farm.name }}
        </button>
        <button
            v-if="hiddenFarms.length"
            ref="overflowButtonRef"
            type="button"
            class="shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors"
            :class="hiddenSelected ? 'border-primary-700 bg-primary-700 text-white' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-100'"
            @click="toggleOverflowPopover"
        >
            +{{ hiddenFarms.length }}
        </button>
        <!-- Off-screen measuring row, identical markup to the visible chips - see remeasureFarmRow. -->
        <div ref="measureRowRef" class="pointer-events-none absolute left-0 top-0 flex items-center gap-2" style="visibility: hidden" aria-hidden="true" inert>
            <button v-for="farm in props.farms" :key="farm.farm_id" type="button" class="shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium">
                {{ farm.name }}
            </button>
            <button type="button" class="shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium">+{{ props.farms.length }}</button>
        </div>
    </div>

    <Teleport to="body">
        <div
            v-if="overflowOpen"
            ref="overflowPopoverRef"
            class="fixed z-[1030] max-h-64 w-56 overflow-y-auto rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg"
            :style="overflowPopoverStyle"
        >
            <button
                v-for="farm in hiddenFarms"
                :key="farm.farm_id"
                type="button"
                class="flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                @click="selectFarm(farm.farm_id)"
            >
                <span class="truncate">{{ farm.name }}</span>
                <Check v-if="isSelected(farm.farm_id)" class="h-4 w-4 shrink-0 text-primary-700" aria-hidden="true" />
            </button>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { Check } from 'lucide-vue-next';
import type { Farm } from '@/types';

const props = defineProps<{
    farms: Farm[];
    modelValue: number | null;
}>();

const emit = defineEmits<{
    'update:modelValue': [number | null];
}>();

function isSelected(farmId: number): boolean {
    return props.modelValue === farmId;
}

// Mutually exclusive: picking a farm always selects it. Re-clicking the already-selected farm is a
// no-op rather than clearing the selection - the rest of the page assumes a farm is always chosen
// once one has been.
function selectFarm(farmId: number): void {
    if (props.modelValue !== farmId) {
        emit('update:modelValue', farmId);
    }
    overflowOpen.value = false;
}

const visibleFarmCount = ref(Infinity);
const visibleFarms = computed(() => props.farms.slice(0, visibleFarmCount.value));
const hiddenFarms = computed(() => props.farms.slice(visibleFarmCount.value));
const hiddenSelected = computed(() => hiddenFarms.value.some((farm) => isSelected(farm.farm_id)));

const farmRowRef = ref<HTMLElement | null>(null);
const measureRowRef = ref<HTMLElement | null>(null);
const overflowButtonRef = ref<HTMLElement | null>(null);
const overflowPopoverRef = ref<HTMLElement | null>(null);
const overflowOpen = ref(false);
const overflowPopoverStyle = ref<{ top: string; left: string }>({ top: '0px', left: '0px' });

function toggleOverflowPopover(): void {
    if (!overflowOpen.value && overflowButtonRef.value) {
        const rect = overflowButtonRef.value.getBoundingClientRect();
        overflowPopoverStyle.value = { top: `${rect.bottom + 4}px`, left: `${rect.right - 224}px` };
    }
    overflowOpen.value = !overflowOpen.value;
}

onClickOutside(
    overflowPopoverRef,
    () => {
        overflowOpen.value = false;
    },
    { ignore: [overflowButtonRef] }
);

// Sums the off-screen measuring row's real chip widths against the visible row's available width,
// reserving room for the "+N" chip unless every farm already fits without it.
function remeasureFarmRow(): void {
    const container = farmRowRef.value;
    const measureEl = measureRowRef.value;
    if (!container || !measureEl || !props.farms.length) return;

    const children = Array.from(measureEl.children) as HTMLElement[];
    const overflowChipWidth = children[children.length - 1].offsetWidth;
    const chipWidths = children.slice(0, -1).map((chip) => chip.offsetWidth);
    const gap = 8;
    const available = container.clientWidth;

    let used = 0;
    let count = 0;
    for (let i = 0; i < chipWidths.length; i += 1) {
        const isLastChip = i === chipWidths.length - 1;
        const next = used + chipWidths[i] + (i > 0 ? gap : 0);
        const reserveForOverflowButton = isLastChip ? 0 : overflowChipWidth + gap;
        if (next + reserveForOverflowButton > available) break;
        used = next;
        count += 1;
    }
    visibleFarmCount.value = count;
}

let farmRowObserver: ResizeObserver | null = null;

onMounted(() => {
    if (!farmRowRef.value) return;
    farmRowObserver = new ResizeObserver(() => remeasureFarmRow());
    farmRowObserver.observe(farmRowRef.value);
});

onUnmounted(() => {
    farmRowObserver?.disconnect();
    farmRowObserver = null;
});

watch(() => props.farms, () => nextTick(remeasureFarmRow));

watch(
    () => hiddenFarms.value.length,
    (count) => {
        if (count === 0) overflowOpen.value = false;
    }
);
</script>
